import React from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown, Copy } from 'lucide-react';

export interface StationCardPreviewProps {
  id: string;
  name: string;
  icon: string;
  buddyType: string;
  description: string;
  badgeColor: string;
  iconBgColor: string;
  textColor: string;
  starters: string[];
  isPreheated?: boolean;
  isWIP?: boolean;
  customColor?: string; // Hex color from meta_config
  customGptUrl?: string; // Custom GPT URL from meta_config
  /** Notify parent when Starters dropdown opens/closes (e.g. to prevent sheet from closing on outside click) */
  onDropdownOpenChange?: (open: boolean) => void;
}

const StationCardPreview: React.FC<StationCardPreviewProps> = ({
  id,
  name,
  icon,
  buddyType,
  description,
  badgeColor,
  iconBgColor,
  textColor,
  starters,
  isPreheated = false,
  isWIP = false,
  customColor,
  customGptUrl,
  onDropdownOpenChange,
}) => {
  const handleCopyStarter = async (e: React.MouseEvent, starter: string) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(starter);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <div className="relative overflow-visible">
      {/* WIP Badge - Outside card, top right corner */}
      {isWIP && (
        <div className="absolute -top-2 -right-2 z-10">
          <span className="px-2 py-1 bg-yellow-400 text-yellow-900 text-xs font-bold rounded group-hover:scale-110 transition-transform duration-300">
            WIP
          </span>
        </div>
      )}
      
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer relative group overflow-visible flex flex-col justify-between" style={{ minHeight: '180px' }}>
        {/* Top Section - Icon, Name, Badge, Description */}
        <div className="flex items-start space-x-3">
          <div 
            className={`w-12 h-12 rounded-full ${iconBgColor || 'bg-white'} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 overflow-hidden border border-gray-200 shadow-sm`}
          >
            {icon && (icon.startsWith('http') || icon.startsWith('data:image')) ? (
              <img
                src={icon}
                alt={`${name} icon`}
                className="w-8 h-8 object-contain group-hover:scale-110 transition-transform duration-300"
              />
            ) : (
              <img
                src="https://storage.googleapis.com/ltp-static-data/drawer-icons/kitchen.svg"
                alt="Station icon"
                className="w-8 h-8 object-contain group-hover:scale-110 transition-transform duration-300"
              />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">{name}</h3>
            <span 
              className={`inline-block px-2 py-0.5 text-black text-xs font-medium rounded-full ${customColor ? '' : badgeColor || 'bg-blue-100'}`}
              style={customColor ? { 
                backgroundColor: customColor,
                color: '#000000'
              } : undefined}
            >
              {buddyType}
            </span>
            <p className="text-sm text-gray-600 mt-2">
              {description}
            </p>
          </div>
        </div>
        
        {/* Bottom Section - Starters (lift-ui-style dropdown with portal for z-index) */}
        {!isPreheated ? (
          <DropdownMenu onOpenChange={onDropdownOpenChange}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="text-sm font-medium hover:opacity-80 transition-all duration-300 flex items-center outline-none"
                style={{ color: '#ff9c95' }}
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                Starters
                <ChevronDown className="w-4 h-4 ml-1" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              sideOffset={6}
              className="w-80 max-h-[16rem] overflow-hidden flex flex-col p-0 z-[100]"
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <div className="max-h-64 overflow-y-auto p-1">
                {starters.length > 0 ? (
                  starters.map((starter, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between gap-2 rounded-sm px-2 py-2 text-sm text-gray-700 hover:bg-accent cursor-default"
                    >
                      <span className="flex-1 min-w-0 truncate">{starter}</span>
                      <button
                        type="button"
                        onClick={(e) => handleCopyStarter(e, starter)}
                        className="p-1.5 rounded hover:bg-accent shrink-0"
                        title="Copy to clipboard"
                      >
                        <Copy className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="px-3 py-2 text-sm text-muted-foreground italic">
                    No starters added yet
                  </div>
                )}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <span className="text-sm font-medium" style={{ color: '#ff9c95' }}>
            Already preheated!
          </span>
        )}

        {/* Hover Effect Overlay */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-transparent via-transparent to-transparent group-hover:from-white/10 group-hover:via-white/5 group-hover:to-transparent transition-all duration-300 pointer-events-none" />
      </div>
    </div>
  );
};

export default StationCardPreview;
